import React, { useEffect, useState } from "react";
import { FileArrowUp, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout from "@/components/MerchantLayout";
import { StatusPill } from "@/components/Layout";
import { KybDocumentGallery } from "@/components/KybDocumentGallery";
import { mApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";
import { formatBytes, KYB_MAX_BYTES, validateKybFile } from "@/lib/files";

const DOC_TYPES = [
  { v: "incorporation", l: "Certificate of Incorporation" },
  { v: "director_id", l: "Director ID / Passport" },
  { v: "proof_of_address", l: "Proof of Address" },
  { v: "bank_statement", l: "Bank Statement" },
  { v: "other", l: "Other" },
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*";

export default function MerchantKyb() {
  const [items, setItems] = useState([]);
  const [docType, setDocType] = useState("incorporation");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [merchantStatus, setMerchantStatus] = useState(null);

  const load = async () => {
    try {
      const [k, me] = await Promise.all([mApi.get("/merchant/kyb"), mApi.get("/merchant/me")]);
      setItems(k.data.items || []);
      setMerchantStatus(me.data.merchant);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not load KYB documents"));
    }
  };
  useEffect(() => { load(); }, []);

  const onFileChange = (next) => {
    setFile(next);
    setUploadError("");
    setFileError(next ? validateKybFile(next) || "" : "");
  };

  const upload = async (e) => {
    e.preventDefault();
    const validationError = validateKybFile(file);
    if (validationError) {
      setFileError(validationError);
      toast.error(validationError);
      return;
    }

    setBusy(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("doc_type", docType);
      formData.append("file", file);
      await mApi.post("/merchant/kyb/upload", formData);
      toast.success(`${file.name} uploaded`);
      setFile(null);
      setFileError("");
      const input = document.querySelector("[data-testid='kyb-file']");
      if (input) input.value = "";
      load();
    } catch (err) {
      const message = apiErrorMessage(err, "Upload failed", { upload: true });
      setUploadError(message);
      toast.error(message);
    } finally { setBusy(false); }
  };

  const canUpload = file && !fileError && !busy;

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-kyb-page">
        <div className="mb-8">
          <h1 className="font-display text-3xl tracking-tight font-medium">KYB Documents</h1>
          <p className="mt-1 text-sm text-zinc-500">Submit verification documents to activate live card issuance.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <StatusCard
            label="Pending"
            count={items.filter((i) => i.status === "submitted").length}
            icon={Clock} color="amber"
          />
          <StatusCard
            label="Approved"
            count={items.filter((i) => i.status === "approved").length}
            icon={CheckCircle} color="emerald"
          />
          <StatusCard
            label="Rejected"
            count={items.filter((i) => i.status === "rejected").length}
            icon={XCircle} color="red"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-sm p-5">
            <div className="font-display text-lg tracking-tight font-medium mb-4">Upload document</div>
            <form onSubmit={upload} className="space-y-3" data-testid="kyb-upload-form" noValidate>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Document type</label>
                <select
                  data-testid="kyb-doc-type"
                  value={docType} onChange={(e) => setDocType(e.target.value)}
                  className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
                >
                  {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">
                  File (PDF or image, max {formatBytes(KYB_MAX_BYTES)})
                </label>
                <input
                  type="file"
                  accept={ACCEPT}
                  data-testid="kyb-file"
                  onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                  className={`w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border file:border-zinc-200 file:bg-white file:text-sm file:text-zinc-900 hover:file:bg-zinc-50 ${
                    fileError ? "text-rose-600" : ""
                  }`}
                />
                {file && !fileError && (
                  <p className="mt-1 text-xs text-zinc-500" data-testid="kyb-file-meta">
                    {file.name} · {formatBytes(file.size)}
                  </p>
                )}
                {fileError && (
                  <p className="mt-1 text-xs text-rose-600" role="alert" data-testid="kyb-file-error">
                    {fileError}
                  </p>
                )}
              </div>

              {uploadError && (
                <div
                  className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                  role="alert"
                  data-testid="kyb-upload-error"
                >
                  {uploadError}
                </div>
              )}

              <button
                type="submit"
                disabled={!canUpload}
                data-testid="kyb-upload-btn"
                className="w-full h-9 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
              >
                <FileArrowUp size={14} /> {busy ? "Uploading…" : "Upload"}
              </button>
            </form>

            {merchantStatus?.kyb_status === "rejected" && merchantStatus?.kyb_rejection_reason && (
              <div className="mt-5 p-3 rounded-sm bg-red-50 border border-red-200 text-xs text-red-800">
                <div className="font-semibold mb-1">Last review: rejected</div>
                {merchantStatus.kyb_rejection_reason}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-5">
            <div className="font-display text-lg tracking-tight font-medium mb-4">Your submissions</div>
            <KybDocumentGallery documents={items} emptyMessage="No documents uploaded yet." />
            {items.length > 0 && (
              <ul className="mt-6 pt-4 border-t border-zinc-100 space-y-2">
                {items.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium truncate">{d.file_name}</span>
                    <StatusPill value={d.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}

function StatusCard({ label, count, icon: Icon, color }) {
  const cls = { amber: "text-amber-600 bg-amber-50", emerald: "text-emerald-600 bg-emerald-50", red: "text-red-600 bg-red-50" }[color];
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</div>
          <div className="font-display text-3xl tracking-tight font-medium mt-2 tabular-nums">{count}</div>
        </div>
        <div className={`h-8 w-8 rounded-sm flex items-center justify-center ${cls}`}><Icon size={16} /></div>
      </div>
    </div>
  );
}
